import { If, onCleanup, onMount, reactive, watch } from 'cdui-js';
import type { JSX } from 'cdui-js';
import type { UserImageVerificationRespDTO } from '../../../../api/login';
import i18n from '../../../../i18n';
import './style.css';

const THUMB_WIDTH = 65;
const STATUS_HOLD_MS = 600;

export type SlideCaptchaSubmitPayload = {
  validateCode: string;
  slideX: number;
};

export type SlideCaptchaProps = {
  visible: boolean;
  captcha: UserImageVerificationRespDTO | null;
  onCancel: () => void;
  /** 松手后由父组件调 verifySend；reject 则组件进入 failure */
  onSubmit: (payload: SlideCaptchaSubmitPayload) => Promise<void>;
  /** 换一张：父组件调 sliderSend 并更新 captcha */
  onRefresh?: () => Promise<void>;
  /** 成功动画结束后回调（父组件关弹层并继续发码后流程） */
  onSuccess?: () => void;
  class?: string;
  style?: JSX.CSSProperties;
};

type CaptchaPhase = 'idle' | 'submitting' | 'success' | 'failure';

const toImageUri = (raw?: string): string => {
  const s = (raw || '').trim();
  if (!s) return '';
  if (s.startsWith('data:')) return s;
  const b64 = s.replace(/\s+/g, '');
  let mime = '';
  if (b64.startsWith('iVBORw0KGgo')) mime = 'image/png';
  else if (b64.startsWith('/9j/')) mime = 'image/jpeg';
  else if (b64.startsWith('R0lGOD')) mime = 'image/gif';
  else if (b64.startsWith('UklGR')) mime = 'image/webp';
  return mime ? `data:${mime};base64,${b64}` : '';
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const computeSlideX = (opts: {
  trackX: number;
  trackTravelMax: number;
  pieceTravelMax: number;
  scale: number;
}) => {
  if (opts.trackTravelMax <= 0 || opts.scale <= 0) return 0;
  const pieceX = (opts.trackX / opts.trackTravelMax) * opts.pieceTravelMax;
  return Math.max(0, Math.round(pieceX / opts.scale));
};

const loadImageNaturalSize = (src: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('empty'));
      return;
    }
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => reject(new Error('load failed'));
    img.src = src;
  });

export const SlideCaptcha = (props: SlideCaptchaProps) => {
  let stageEl: HTMLDivElement | null = null;
  let trackEl: HTMLDivElement | null = null;
  let statusTimer: ReturnType<typeof setTimeout> | null = null;

  const state = reactive({
    phase: 'idle' as CaptchaPhase,
    trackX: 0,
    scale: 1,
    viewportWidth: 0,
    viewportHeight: 0,
    pieceNaturalWidth: 0,
    pieceNaturalHeight: 0,
    sourceWidth: 0,
    sourceHeight: 0,
    trackWidth: 0,
    loadError: false,
    dragging: false,
    failureText: i18n.Login.SlideCaptchaVerifyFailed,
  });

  let dragStartPointerX = 0;
  let dragStartTrackX = 0;
  let activePointerId: number | null = null;

  const clearStatusTimer = () => {
    if (statusTimer) {
      clearTimeout(statusTimer);
      statusTimer = null;
    }
  };

  const canInteract = () =>
    props.visible &&
    state.phase === 'idle' &&
    !state.loadError &&
    !!props.captcha?.validateCode;

  const pieceDisplayWidth = () => Math.max(0, state.pieceNaturalWidth * state.scale);
  const pieceDisplayHeight = () => Math.max(0, state.pieceNaturalHeight * state.scale);
  const pieceTravelMax = () => Math.max(0, state.viewportWidth - pieceDisplayWidth());
  const trackTravelMax = () => Math.max(0, state.trackWidth - THUMB_WIDTH);
  const pieceTop = () => Math.max(0, (props.captcha?.y ?? 0) * state.scale);
  const pieceXDisplay = () => {
    const max = trackTravelMax();
    if (max <= 0) return 0;
    return (state.trackX / max) * pieceTravelMax();
  };

  const shadeSrc = () => toImageUri(props.captcha?.shadeImage);
  const pieceSrc = () => toImageUri(props.captcha?.cutoutImage);

  const resetMotion = () => {
    state.trackX = 0;
  };

  const measureLayout = async () => {
    const captcha = props.captcha;
    if (!captcha) return;
    if (!stageEl || !trackEl) {
      requestAnimationFrame(() => {
        void measureLayout();
      });
      return;
    }

    const shade = shadeSrc();
    const piece = pieceSrc();
    if (!shade || !piece) {
      state.loadError = true;
      return;
    }

    state.loadError = false;
    try {
      const [shadeSize, pieceSize] = await Promise.all([
        loadImageNaturalSize(shade),
        loadImageNaturalSize(piece),
      ]);

      state.sourceWidth = captcha.width && captcha.width > 0 ? captcha.width : shadeSize.width;
      state.sourceHeight = captcha.height && captcha.height > 0 ? captcha.height : shadeSize.height;
      state.pieceNaturalWidth = pieceSize.width;
      state.pieceNaturalHeight = pieceSize.height;

      const viewportWidth = stageEl.clientWidth || 0;
      state.viewportWidth = viewportWidth;
      state.trackWidth = trackEl.clientWidth || 0;

      if (state.sourceWidth > 0) {
        state.scale = viewportWidth / state.sourceWidth;
        state.viewportHeight =
          state.sourceHeight > 0 ? state.sourceHeight * state.scale : viewportWidth * 0.5;
      } else {
        state.scale = 1;
        state.viewportHeight = viewportWidth * 0.5;
      }

      resetMotion();
      state.phase = 'idle';
    } catch {
      state.loadError = true;
    }
  };

  const applyResize = () => {
    if (!props.visible || !stageEl || !trackEl || state.sourceWidth <= 0) return;
    const viewportWidth = stageEl.clientWidth || 0;
    state.viewportWidth = viewportWidth;
    state.trackWidth = trackEl.clientWidth || 0;
    state.scale = viewportWidth / state.sourceWidth;
    state.viewportHeight =
      state.sourceHeight > 0 ? state.sourceHeight * state.scale : viewportWidth * 0.5;
    resetMotion();
  };

  const finishSuccess = () => {
    clearStatusTimer();
    state.phase = 'success';
    statusTimer = setTimeout(() => {
      props.onSuccess?.();
    }, STATUS_HOLD_MS);
  };

  const finishFailure = (autoRefresh: boolean) => {
    clearStatusTimer();
    state.phase = 'failure';
    resetMotion();
    statusTimer = setTimeout(async () => {
      state.phase = 'idle';
      if (autoRefresh && props.onRefresh) {
        try {
          await props.onRefresh();
        } catch {
          state.loadError = true;
        }
      }
    }, STATUS_HOLD_MS);
  };

  const submitCurrent = async () => {
    if (!canInteract()) return;
    const validateCode = props.captcha?.validateCode?.trim();
    if (!validateCode) {
      state.loadError = true;
      return;
    }

    const slideX = computeSlideX({
      trackX: state.trackX,
      trackTravelMax: trackTravelMax(),
      pieceTravelMax: pieceTravelMax(),
      scale: state.scale,
    });

    state.phase = 'submitting';
    try {
      await props.onSubmit({ validateCode, slideX });
      finishSuccess();
    } catch (e) {
      // 仅滑块业务失败（10088/10089）自动换图；网络等其它错误不自动刷新
      const msg = typeof e === 'string' ? e : String((e as any)?.message ?? '');
      const autoRefresh = /^(10088|10089)\b/.test(msg);
      state.failureText = i18n.Login.SlideCaptchaVerifyFailed;
      finishFailure(autoRefresh);
    }
  };

  const onPointerDown = (e: PointerEvent) => {
    if (!canInteract()) return;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture?.(e.pointerId);
    activePointerId = e.pointerId;
    state.dragging = true;
    dragStartPointerX = e.clientX;
    dragStartTrackX = state.trackX;
    e.preventDefault();
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!state.dragging || (activePointerId != null && e.pointerId !== activePointerId)) return;
    const next = dragStartTrackX + (e.clientX - dragStartPointerX);
    state.trackX = clamp(next, 0, trackTravelMax());
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!state.dragging || (activePointerId != null && e.pointerId !== activePointerId)) return;
    state.dragging = false;
    activePointerId = null;
    void submitCurrent();
  };

  const handleCancel = () => {
    if (state.phase === 'submitting' || state.phase === 'success') return;
    props.onCancel();
  };

  const handleRefresh = async () => {
    if (!canInteract() || !props.onRefresh) return;
    state.phase = 'submitting';
    try {
      await props.onRefresh();
      state.phase = 'idle';
    } catch {
      state.loadError = true;
      state.phase = 'idle';
    }
  };

  watch(
    () => props.visible,
    (visible) => {
      clearStatusTimer();
      if (!visible) {
        state.phase = 'idle';
        resetMotion();
        return;
      }
      void measureLayout();
    },
  );

  watch(
    () => props.captcha?.validateCode,
    () => {
      if (!props.visible) return;
      clearStatusTimer();
      void measureLayout();
    },
  );

  onMount(() => {
    const onWinResize = () => applyResize();
    window.addEventListener('resize', onWinResize);
    if (props.visible) void measureLayout();
    onCleanup(() => {
      window.removeEventListener('resize', onWinResize);
      clearStatusTimer();
    });
  });

  return (
    <If when={props.visible}>
      <div
        class={`slide-captcha-mask ${props.class ?? ''}`.trim()}
        style={props.style}
        onclick={() => handleCancel()}
      >
        <div
          class="slide-captcha-card"
          role="dialog"
          aria-modal="true"
          aria-label={i18n.Login.SlideCaptchaHint}
          onclick={(e) => e.stopPropagation()}
        >
          <div class="slide-captcha-title">{i18n.Login.SlideCaptchaHint}</div>

          <div
            class="slide-captcha-stage"
            ref={(el) => (stageEl = el)}
            style={{
              height: state.viewportHeight > 0 ? `${state.viewportHeight}px` : '120px',
            }}
          >
            <If when={!!shadeSrc() && !state.loadError}>
              <img class="slide-captcha-shade" src={shadeSrc()} alt="" draggable={false} />
            </If>
            <If when={!!pieceSrc() && !state.loadError}>
              <img
                class="slide-captcha-piece"
                src={pieceSrc()}
                alt=""
                draggable={false}
                style={{
                  width: `${pieceDisplayWidth()}px`,
                  height: `${pieceDisplayHeight()}px`,
                  top: `${pieceTop()}px`,
                  transform: `translateX(${pieceXDisplay()}px)`,
                }}
              />
            </If>

            <If when={state.phase === 'success'}>
              <div class="slide-captcha-overlay slide-captcha-overlay-success">
                {i18n.Login.SlideCaptchaSuccess}
              </div>
            </If>
            <If when={state.phase === 'failure'}>
              <div class="slide-captcha-overlay slide-captcha-overlay-failure">
                {state.failureText}
              </div>
            </If>
            <If when={state.loadError}>
              <div class="slide-captcha-stage-tip">{i18n.Login.SlideCaptchaLoadFailed}</div>
            </If>
          </div>

          <div
            class="slide-captcha-track"
            ref={(el) => (trackEl = el)}
            onpointerdown={onPointerDown}
            onpointermove={onPointerMove}
            onpointerup={onPointerUp}
            onpointercancel={onPointerUp}
          >
            <div
              class="slide-captcha-thumb"
              style={{ transform: `translateX(${state.trackX}px)` }}
            >
              <div class="slide-captcha-thumb-bar" />
            </div>
          </div>

          <div class="slide-captcha-actions">
            <button
              type="button"
              class="slide-captcha-action-btn"
              aria-label="close"
              disabled={state.phase === 'submitting' || state.phase === 'success'}
              onclick={() => handleCancel()}
            >
              ✕
            </button>
            <button
              type="button"
              class="slide-captcha-action-btn"
              aria-label="refresh"
              disabled={!canInteract() || !props.onRefresh}
              onclick={() => void handleRefresh()}
            >
              ↻
            </button>
          </div>
        </div>
      </div>
    </If>
  );
};
